import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import * as d3 from 'd3'
import type { JTBDNode } from '../data'
import './AITreemap.css'

interface AITreemapProps {
  data: JTBDNode
  theme: 'light' | 'dark'
  onNodeClick: (path: string[]) => void
  portalContainer?: HTMLElement | null
}

type LayoutNode = d3.HierarchyRectangularNode<JTBDNode> & {
  satisfactionScore?: number
}

const getNodeScore = (node: LayoutNode): number => node.satisfactionScore ?? node.data.satisfactionScore ?? 3

const textMeasureCtx = (() => {
  let ctx: CanvasRenderingContext2D | null = null
  return () => {
    if (!ctx) ctx = document.createElement('canvas').getContext('2d')!
    return ctx
  }
})()

const textFits = (text: string, fontSize: number, fontWeight: number, maxWidth: number): boolean => {
  if (maxWidth <= 0) return false
  const ctx = textMeasureCtx()
  ctx.font = `${fontWeight} ${fontSize}px system-ui, sans-serif`
  return ctx.measureText(text).width <= maxWidth
}

const DEFAULT_PADDING_TOP: Record<number, number> = { 1: 28, 2: 24, 3: 20 }
const MIN_LABEL_HEIGHT: Record<number, number> = { 1: 22, 2: 16, 3: 14 }

const getActualPaddingTop = (depth: number, nodeHeight: number): number => {
  const defaultPad = DEFAULT_PADDING_TOP[depth] || 0
  if (defaultPad === 0) return 0
  const maxPad = Math.floor(nodeHeight * 0.4)
  const actualPad = Math.min(defaultPad, maxPad)
  return actualPad < (MIN_LABEL_HEIGHT[depth] || 0) ? 0 : actualPad
}

export default function AITreemap({
  data,
  theme,
  onNodeClick,
  portalContainer,
}: AITreemapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const suppressHoverRef = useRef(false)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [hoveredNode, setHoveredNode] = useState<LayoutNode | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null)

  const { width, height } = dimensions

  const computeTooltipPosition = useCallback(() => {
    if (!hoveredNode || !tooltipRef.current || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const tw = tooltipRef.current.offsetWidth
    const th = tooltipRef.current.offsetHeight
    const gap = 10
    const pad = 8

    // 节点在视口中的坐标
    const nodeRight = rect.left + hoveredNode.x1
    const nodeLeft = rect.left + hoveredNode.x0

    // 约束范围：云图容器边界
    const areaLeft = rect.left
    const areaRight = rect.right
    const areaTop = rect.top
    const areaBottom = rect.bottom

    // 水平：优先放节点右侧，放不下则左侧，都不够则贴容器边缘
    let left: number
    const rightSpace = areaRight - nodeRight
    const leftSpace = nodeLeft - areaLeft
    if (rightSpace >= tw + gap) {
      left = nodeRight + gap
    } else if (leftSpace >= tw + gap) {
      left = nodeLeft - gap - tw
    } else {
      // 两侧都放不下，贴容器右边缘
      left = Math.max(areaLeft + pad, areaRight - tw - pad)
    }

    // 垂直：以节点顶部对齐，约束在容器范围内
    const nodeTop = rect.top + hoveredNode.y0
    const top = Math.max(areaTop + pad, Math.min(nodeTop, areaBottom - th - pad))

    setTooltipPos({ left, top })
  }, [hoveredNode, width, height])

  useEffect(() => {
    if (hoveredNode) {
      setTooltipPos(null) // 重置，触发隐藏直到位置计算完成
      requestAnimationFrame(computeTooltipPosition)
    }
  }, [hoveredNode, computeTooltipPosition])

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const root = useMemo(() => {
    if (width === 0 || height === 0) {
      return null
    }

    const hierarchy = d3
      .hierarchy<JTBDNode>(data)
      .sum((d) => d.mentionCount || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0))

    hierarchy.eachAfter((node) => {
      const current = node as LayoutNode
      if (node.children) {
        let totalWeightedScore = 0
        node.children.forEach((childNode) => {
          const child = childNode as LayoutNode
          totalWeightedScore += (child.value || 0) * getNodeScore(child)
        })
        const nodeValue = node.value ?? 0
        current.satisfactionScore = nodeValue === 0 ? 3 : totalWeightedScore / nodeValue
      } else {
        current.satisfactionScore = node.data.satisfactionScore
      }
    })

    const treemapLayout = d3
      .treemap<JTBDNode>()
      .size([width, height])
      .tile((node, x0, y0, x1, y1) => {
        if (node.depth === 0) {
          d3.treemapDice(node, x0, y0, x1, y1)
        } else {
          d3.treemapSquarify(node, x0, y0, x1, y1)
        }
      })
      .paddingTop((d) => getActualPaddingTop(d.depth, d.y1 - d.y0))
      .paddingRight((d) => d.depth >= 1 ? 0 : 1)
      .paddingBottom((d) => d.depth >= 1 ? 0 : 1)
      .paddingLeft((d) => d.depth >= 1 ? 0 : 1)
      .paddingInner((d) => d.depth === 0 ? 2.5 : 1)
      .round(true)

    return treemapLayout(hierarchy) as LayoutNode
  }, [data, width, height])

  // 数据或尺寸变化时抑制悬停，防止 treemap DOM 重建后浏览器触发的假 mouseenter
  useEffect(() => {
    suppressHoverRef.current = true
    setHoveredNode(null)
    setTooltipPos(null)
  }, [data, width, height])

  const getColor = (score: number): string => {
    if (score <= 2) return '#d72426'
    if (score <= 3) return '#e88a3a'
    if (score <= 4) return '#e8c63a'
    if (score <= 4.5) return '#6ab04c'
    return '#27864a'
  }

  const formatScore = (score: number): string => score.toFixed(1)

  const getNonLeafBg = (depth: number) => {
    if (theme === 'dark') {
      return depth === 1 ? '#333' : '#2a2a2a'
    }
    return depth === 1 ? '#ccc' : '#d9d9d9'
  }

  if (!root) {
    return <div ref={containerRef} className={`ai-treemap ai-treemap-${theme}`} />
  }

  return (
    <div ref={containerRef} className={`ai-treemap ai-treemap-${theme}`} onMouseMove={() => { suppressHoverRef.current = false }}>
      {root.descendants().map((node, index) => {
        const current = node as LayoutNode
        const isLeaf = !current.children
        const depth = current.depth

        if (depth === 0 && !isLeaf) {
          return null
        }

        const nodeWidth = current.x1 - current.x0
        const nodeHeight = current.y1 - current.y0
        if (nodeWidth <= 0 || nodeHeight <= 0) {
          return null
        }

        const score = getNodeScore(current)
        const isDirectChildOfHovered = Boolean(hoveredNode && current.parent === hoveredNode && !isLeaf)
        const isLeafBlackText = Boolean(
          hoveredNode &&
          isLeaf &&
          (
            (current.parent === hoveredNode && hoveredNode.children?.every(c => !c.children)) ||
            current === hoveredNode
          )
        )
        const leafNeedsDarkText = false

        return (
          <div
            key={`${current.data.name}-${index}`}
            className={`ai-node ${theme === 'dark' ? 'dark' : 'light'}`}
            style={{
              left: current.x0,
              top: current.y0,
              width: nodeWidth,
              height: nodeHeight,
              backgroundColor: isLeaf ? getColor(score) : getNonLeafBg(depth),
              zIndex: depth,
              boxShadow: current === hoveredNode ? 'inset 0 0 0 9999px rgba(22, 119, 255, 0.18)' : undefined,
            }}
            onClick={(event) => {
              event.stopPropagation()
              const path = current.ancestors().reverse().map((ancestor) => ancestor.data.name).slice(1)
              onNodeClick(path)
            }}
            onMouseEnter={() => { if (!suppressHoverRef.current) setHoveredNode(current) }}
            onMouseLeave={() => setHoveredNode(null)}
          >
            {!isLeaf && depth === 1 && getActualPaddingTop(depth, nodeHeight) > 0 && nodeWidth > 40 && nodeHeight > 24 && textFits(current.data.name, 16, 700, nodeWidth - 10) && (
              <div className={`ai-node-label ai-node-label-1 ${isDirectChildOfHovered ? 'is-blue' : ''}`}>
                {current.data.name}
              </div>
            )}

            {!isLeaf && depth === 2 && getActualPaddingTop(depth, nodeHeight) > 0 && nodeWidth > 30 && nodeHeight > 20 && textFits(current.data.name, 14, 500, nodeWidth - 10) && (
              <div className={`ai-node-label ai-node-label-2 ${isDirectChildOfHovered ? 'is-blue' : ''}`}>
                {current.data.name}
              </div>
            )}

            {!isLeaf && depth === 3 && getActualPaddingTop(depth, nodeHeight) > 0 && nodeWidth > 20 && nodeHeight > 16 && textFits(current.data.name, 12, 400, nodeWidth - 10) && (
              <div className={`ai-node-label ai-node-label-3 ${isDirectChildOfHovered ? 'is-blue' : ''}`}>
                {current.data.name}
              </div>
            )}

            {(() => {
              if (!isLeaf || nodeWidth <= 40 || nodeHeight <= 30) return null
              const nameFontSize = Math.min(14, nodeWidth / 4, nodeHeight / 3)
              const availWidth = nodeWidth - 10
              if (!textFits(current.data.name, nameFontSize, 500, availWidth)) return null

              const valueFontSize = Math.min(12, nodeWidth / 5, nodeHeight / 4)
              const valueText = `${current.value?.toLocaleString()}/${formatScore(score)}`
              const showValue = nodeWidth > 50 && nodeHeight > 40 && textFits(valueText, valueFontSize, 400, availWidth)

              return (
                <div className={`ai-leaf-content ${isLeafBlackText || leafNeedsDarkText ? 'is-black' : ''}`}>
                  <div
                    className="ai-leaf-name"
                    style={{ fontSize: `${nameFontSize}px` }}
                  >
                    {current.data.name}
                  </div>

                  {showValue && (
                    <div
                      className="ai-leaf-value"
                      style={{ fontSize: `${valueFontSize}px` }}
                    >
                      {valueText}
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        )
      })}

      {hoveredNode && (
        <>
          {hoveredNode.children?.map((childNode, index) => (
            <div
              key={`child-highlight-${childNode.data.name}-${index}`}
              className="ai-highlight-child"
              style={{
                left: childNode.x0,
                top: childNode.y0,
                width: childNode.x1 - childNode.x0,
                height: childNode.y1 - childNode.y0,
                boxShadow:
                  theme === 'dark'
                    ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.5)'
                    : 'inset 0 0 0 1px rgba(0, 0, 0, 0.5)',
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
              }}
            />
          ))}

          <div
            className="ai-highlight-parent"
            style={{
              left: hoveredNode.x0,
              top: hoveredNode.y0,
              width: hoveredNode.x1 - hoveredNode.x0,
              height: hoveredNode.y1 - hoveredNode.y0,
              boxShadow:
                theme === 'dark'
                  ? 'inset 0 0 0 2px rgba(255, 255, 255, 0.8)'
                  : 'inset 0 0 0 2px rgba(0, 0, 0, 0.8)',
              backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            }}
          />
        </>
      )}

      {hoveredNode && createPortal(
          <div
            ref={tooltipRef}
            className={`ai-tooltip ai-tooltip-${theme}`}
            style={tooltipPos
              ? { position: 'fixed', left: tooltipPos.left, top: tooltipPos.top }
              : { position: 'fixed', visibility: 'hidden' as const }
            }
          >
            <div className="ai-tooltip-header">
              <div className="ai-tooltip-title">{hoveredNode.data.name}</div>
              {hoveredNode.data.description && (
                <div className="ai-tooltip-path">{hoveredNode.data.description}</div>
              )}
            </div>

            <div className="ai-tooltip-grid">
              <span className="ai-tooltip-key">提及频率 (VOC):</span>
              <span className="ai-tooltip-val">{hoveredNode.value?.toLocaleString()}</span>

              <span className="ai-tooltip-key">满意度:</span>
              <span className="ai-tooltip-val" style={{ color: getColor(getNodeScore(hoveredNode)) }}>
                {formatScore(getNodeScore(hoveredNode))} / 5.0
              </span>
            </div>

            {hoveredNode.children && hoveredNode.children.length > 0 && (
              <div className="ai-tooltip-children-wrap">
                <div className="ai-tooltip-children-title">E{hoveredNode.depth + 1}详情对比</div>
                <div className="ai-tooltip-children-list">
                  {hoveredNode.children.map((childNode) => {
                    const child = childNode as LayoutNode
                    const childScore = getNodeScore(child)
                    const childColor = getColor(childScore)
                    return (
                      <div key={child.data.name} className="ai-tooltip-child-item">
                        <div className="ai-tooltip-child-left">
                          <span className="ai-tooltip-dot" style={{ backgroundColor: childColor }} />
                          <span className="ai-tooltip-child-name">{child.data.name}</span>
                        </div>
                        <div className="ai-tooltip-child-right">
                          <span>{child.value?.toLocaleString()}</span>
                          <span style={{ color: childColor }}>{formatScore(childScore)}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>,
        portalContainer || document.body,
      )}
    </div>
  )
}
